import React, { useMemo, useState, useEffect } from "react";
import { MdLaptopMac, MdPhoneIphone, MdPhoneAndroid, MdVisibilityOff } from "react-icons/md";
import { FiAlertTriangle } from "react-icons/fi";

/**
 * AdSimulation — "where will this banner ACTUALLY appear, and how?"
 *
 * Aug 29 2026: the hero slot carries TWO artworks (phone 16:9 in imageUrl,
 * desktop web 3159×498 in desktopImageUrl) and deviceType is derived from
 * which are present, so the tabs follow the artwork.
 *
 * Surface-accurate: a banner is only previewed on the surfaces the platform
 * will really serve it to, derived from (slot × deviceType) exactly the way
 * the clients derive it:
 *
 *   • Mobile app (Android & iOS)   — apps fetch `deviceType=mobile`, so they
 *     receive banners targeted "mobile" or "all", never "desktop".
 *       - hero_carousel  → Home screen rounded hero card (HomePage.js,
 *         16:9, borderRadius 28, in-card dots)
 *       - retail_carousel → Shop screen strip (RetailVendingScreen, 2:1,
 *         bottom scrim with title + description)
 *   • Mobile web (lusuku.shop on a phone) — LandingPage fetches ALL banners
 *     for the slot and splits by viewport: mobile-width shows banners
 *     targeted "mobile" or "all". Full-bleed 16:9 slide (MobileSlideAspect),
 *     slick dots, CTA pill from `buttonText`.
 *   • Desktop web — viewport-split the other way: "desktop" or "all".
 *     Full-width strip, side chevrons, CTA pill bottom-right (BannerButton:
 *     #334d55 pill, bottom 20 / right 24).
 *
 * A combination that renders NOWHERE (e.g. retail_carousel + Desktop web,
 * or any reserved slot) shows an explicit warning instead of a pretty lie.
 *
 * The CTA pill is shown only when the web clients would actually show it:
 * linkType "category" or "external" AND a non-empty buttonText. The mobile
 * APP hero deliberately renders no CTA — the whole card is the tap target.
 */

const SLOT_ASPECT = {
  hero_carousel: 16 / 9,
  retail_carousel: 2,
  deals_banner: 21 / 9,
  secondary_promo: 1,
  category_card: 4 / 3,
  checkout_promo: 16 / 9,
};

// Slots with a real renderer today. Keep in lock-step with SLOT_META.reserved
// in AdvertManager.js.
const LIVE_SLOTS = ["hero_carousel", "retail_carousel"];

const PHONE_W = 300;
const PHONE_GUTTER = Math.round((18 / 390) * PHONE_W);

// ── Desktop web geometry — mirrors the live landing page, not guessed ───────
// Since 23 Aug 2026 LandingPage's `heroFrame` is a FIXED strip:
// `aspect-ratio: 3159 / 498; max-height: 280px; object-fit: cover`. The frame
// no longer takes its height from the artwork, so anything that is not
// 6.34:1 is cover-cropped: a 16:9 file shows a band 28% of its height tall,
// a wider-than-6.34:1 file loses its sides. The page draws
// `desktopImageUrlWebp || desktopImageUrl || imageUrl`.
//   visibleFraction = artworkAspect / FRAME_ASPECT   (artwork taller than frame)
//                   = FRAME_ASPECT / artworkAspect   (artwork wider than frame)
const DESKTOP_FRAME_ASPECT = 3159 / 498;

const SURFACE_META = {
  app: { label: "Mobile app", icon: <MdPhoneIphone size={14} /> },
  mobileweb: { label: "Mobile web", icon: <MdPhoneAndroid size={14} /> },
  desktopweb: { label: "Desktop web", icon: <MdLaptopMac size={14} /> },
};

// The single source of truth for "where does this banner serve?" — mirrors
// the clients' fetch/filter logic, not wishful thinking.
export const eligibleSurfaces = (slot, deviceType) => {
  const dt = deviceType || "all";
  if (!LIVE_SLOTS.includes(slot)) return [];
  const surfaces = [];
  if (slot === "hero_carousel") {
    if (dt !== "desktop") surfaces.push("app", "mobileweb");
    if (dt !== "mobile") surfaces.push("desktopweb");
  } else if (slot === "retail_carousel") {
    if (dt !== "desktop") surfaces.push("app");
  }
  return surfaces;
};

const phoneFrame = (children) => (
  <div
    style={{
      width: PHONE_W,
      borderRadius: 34,
      border: "10px solid #1a1d21",
      background: "#f7f8fa",
      overflow: "hidden",
      boxShadow: "0 12px 32px rgba(26,43,74,0.25)",
      margin: "0 auto",
    }}
  >
    <div style={{ width: 110, height: 18, background: "#1a1d21", borderRadius: "0 0 12px 12px", margin: "0 auto" }} />
    {children}
  </div>
);

const Dots = ({ light = true }) => (
  <div style={{ display: "flex", gap: 5, justifyContent: "center" }}>
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        style={{
          width: i === 0 ? 16 : 6,
          height: 6,
          borderRadius: 3,
          background: light ? "#ffffff" : "#515151",
          opacity: i === 0 ? 1 : 0.45,
        }}
      />
    ))}
  </div>
);

// Mirrors LandingPage's BannerButton (#334d55 pill, bottom-right), scaled.
const CtaPill = ({ text, small = false }) => (
  <span
    style={{
      position: "absolute",
      bottom: small ? 8 : 12,
      right: small ? 8 : 14,
      background: "#334d55",
      color: "#fff",
      fontSize: small ? 9 : 11,
      fontWeight: 600,
      padding: small ? "4px 8px" : "6px 11px",
      borderRadius: 8,
      boxShadow: "1px 1px 6px rgba(55,250,95,0.55)",
      maxWidth: "60%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {text}
  </span>
);

const AdSimulation = ({
  imageUrl,
  imageUrlWebp = "",
  desktopImageUrl = "",
  slot = "hero_carousel",
  title = "",
  description = "",
  deviceType = "all",
  linkType = "none",
  buttonText = "",
}) => {
  const surfaces = useMemo(
    () => eligibleSurfaces(slot, deviceType),
    [slot, deviceType],
  );
  const [surface, setSurface] = useState(surfaces[0] || null);

  // What desktop web will actually draw: its own artwork when the banner has
  // one, otherwise the phone artwork (same fallback chain as the page).
  const desktopArt = desktopImageUrl || imageUrl || "";
  const desktopUsesFallback = !desktopImageUrl && !!imageUrl;

  // Natural dimensions of the desktop artwork — drives how much of it
  // survives the fixed-strip cover crop.
  const [artDims, setArtDims] = useState(null);
  useEffect(() => {
    setArtDims(null);
    if (!desktopArt) return undefined;
    const probe = new Image();
    probe.onload = () =>
      setArtDims({ w: probe.naturalWidth, h: probe.naturalHeight });
    probe.src = desktopArt;
    return () => {
      probe.onload = null;
    };
  }, [desktopArt]);

  // Re-pick a valid tab when slot/deviceType edits change the eligible set
  // (e.g. the admin flips Device Target to "Desktop web only" mid-form).
  useEffect(() => {
    if (!surfaces.includes(surface)) setSurface(surfaces[0] || null);
  }, [surfaces, surface]);

  if (!imageUrl && !desktopImageUrl) return null;

  const aspect = SLOT_ASPECT[slot] || 16 / 9;
  // Web clients show the CTA only for these link types with a buttonText.
  const showsWebCta =
    (linkType === "category" || linkType === "external") && !!buttonText;

  // The app + mobile web render the slot-normalized webp (exact slot aspect);
  // desktop web renders the RAW imageUrl — mirror both faithfully.
  const cardImage = imageUrlWebp || imageUrl;

  // How much of the desktop artwork survives the fixed 6.34:1 cover crop.
  const artAspect = artDims ? artDims.w / artDims.h : DESKTOP_FRAME_ASPECT;
  const artTallerThanFrame = artAspect < DESKTOP_FRAME_ASPECT;
  const desktopVisibleFraction = artTallerThanFrame
    ? artAspect / DESKTOP_FRAME_ASPECT
    : DESKTOP_FRAME_ASPECT / artAspect;
  const desktopVisiblePct = Math.round(desktopVisibleFraction * 100);

  // ── Nothing renders this combination — say so instead of pretending ──
  if (!surfaces.length) {
    const reason = !LIVE_SLOTS.includes(slot)
      ? `The "${slot}" slot is reserved — no app or web screen renders it yet.`
      : `The "${slot}" slot only exists in the mobile apps, but this banner targets Desktop web — so no surface will ever show it.`;
    return (
      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
          background: "#fff4e6",
          border: "1px solid #f3d9b4",
          borderRadius: 8,
          padding: "12px 14px",
        }}
      >
        <MdVisibilityOff size={18} style={{ color: "#f6961e", flexShrink: 0 }} />
        <div style={{ fontSize: 12, lineHeight: 1.5, color: "#4a5568" }}>
          <strong style={{ display: "block", color: "#1a2b4a", marginBottom: 2 }}>
            This banner will not appear anywhere.
          </strong>
          {reason} Change the slot or the device target to give it a surface.
        </div>
      </div>
    );
  }

  const tabStyle = (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    borderRadius: 999,
    border: `1px solid ${active ? "#2c7be5" : "#e2e8f0"}`,
    background: active ? "#eaf2ff" : "transparent",
    color: active ? "#2c7be5" : "#8898aa",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  });

  // ── Mobile APP — Home hero card or Shop retail strip ──
  const appPreview = phoneFrame(
    <>
      <div style={{ padding: `10px ${PHONE_GUTTER}px 6px` }}>
        <div style={{ width: 110, height: 10, borderRadius: 5, background: "#dfe5ec" }} />
        <div style={{ width: 70, height: 8, borderRadius: 4, background: "#e9edf2", marginTop: 6 }} />
      </div>
      <div style={{ padding: `6px ${PHONE_GUTTER}px 14px` }}>
        <div
          style={{
            position: "relative",
            borderRadius: slot === "hero_carousel" ? 22 : 12,
            overflow: "hidden",
            aspectRatio: `${aspect}`,
            background: "#e2e8e7",
          }}
        >
          <img
            src={cardImage}
            alt={title || "Banner preview"}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
          {slot === "retail_carousel" && (title || description) && (
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                padding: "16px 10px 8px",
                background: "linear-gradient(transparent, rgba(0,0,0,0.62))",
                color: "#fff",
              }}
            >
              {title && <div style={{ fontSize: 11, fontWeight: 700 }}>{title}</div>}
              {description && (
                <div style={{ fontSize: 9, opacity: 0.85, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {description}
                </div>
              )}
            </div>
          )}
          {slot === "hero_carousel" && (
            <div style={{ position: "absolute", bottom: 7, left: 0, right: 0 }}>
              <Dots />
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, padding: `0 ${PHONE_GUTTER}px 16px` }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ flex: 1 }}>
            <div style={{ aspectRatio: "1", borderRadius: "50%", background: "#e9edf2" }} />
            <div style={{ height: 7, borderRadius: 4, background: "#e9edf2", marginTop: 6 }} />
          </div>
        ))}
      </div>
    </>,
  );

  // ── Mobile WEB — full-bleed 16:9 slide in the phone browser ──
  const mobileWebPreview = phoneFrame(
    <>
      {/* Mini lusuku.shop mobile navbar */}
      <div style={{ background: "#2e4b56", padding: "8px 10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
          <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#3d9448" }} />
          <div style={{ flex: 1 }} />
          <div style={{ width: 44, height: 12, borderRadius: 6, background: "rgba(255,255,255,0.35)" }} />
          <div style={{ width: 44, height: 12, borderRadius: 6, background: "rgba(255,255,255,0.35)" }} />
        </div>
        <div style={{ height: 20, borderRadius: 10, background: "#ffffff", opacity: 0.92 }} />
      </div>
      {/* Full-bleed slide — MobileSlideAspect locks 16:9, dark letterbox bg */}
      <div style={{ position: "relative", aspectRatio: "16 / 9", background: "#111" }}>
        <img
          src={cardImage}
          alt={title || "Banner preview"}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
        {showsWebCta && <CtaPill text={buttonText} small />}
        <div style={{ position: "absolute", bottom: 5, left: 0, right: 0 }}>
          <Dots />
        </div>
      </div>
      {/* Quick category cards stand-in */}
      <div style={{ display: "flex", gap: 8, padding: "10px 12px 16px" }}>
        {[0, 1].map((i) => (
          <div key={i} style={{ flex: 1, borderRadius: 8, background: "#fff", border: "1px solid #e2e8f0", padding: 8 }}>
            <div style={{ aspectRatio: "1.6", borderRadius: 6, background: "#e9edf2" }} />
            <div style={{ height: 7, borderRadius: 4, background: "#e9edf2", marginTop: 6, width: "70%" }} />
          </div>
        ))}
      </div>
    </>,
  );

  // ── Desktop WEB — landing hero strip with chevrons + CTA pill ──
  const desktopWebPreview = (
    <div>
      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: 10,
          overflow: "hidden",
          boxShadow: "0 8px 24px rgba(26,43,74,0.12)",
        }}
      >
        <div style={{ background: "#2e4b56", padding: "10px 16px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#3d9448" }} />
          <div style={{ flex: 1, maxWidth: 320, height: 22, borderRadius: 11, background: "#ffffff", opacity: 0.9 }} />
          <div style={{ width: 54, height: 16, borderRadius: 8, background: "rgba(255,255,255,0.35)" }} />
        </div>
        {/* The live frame: a fixed 3159×498 strip, object-fit cover on the
            desktop artwork (phone artwork when there is none). */}
        <div style={{ position: "relative", aspectRatio: `${DESKTOP_FRAME_ASPECT}`, background: "#e2e8e7" }}>
          <img
            src={desktopArt}
            alt={title || "Banner preview"}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }}
          />
          {["left", "right"].map((side) => (
            <div
              key={side}
              style={{
                position: "absolute",
                [side]: 8,
                top: "50%",
                transform: "translateY(-50%)",
                width: 26,
                height: 26,
                borderRadius: "50%",
                background: "rgba(0,0,0,0.55)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              {side === "left" ? "‹" : "›"}
            </div>
          ))}
          {showsWebCta && <CtaPill text={buttonText} />}
          <div style={{ position: "absolute", bottom: 8, left: 0, right: 0 }}>
            <Dots />
          </div>
        </div>
      </div>
      {/* Honest crop warning: the frame is a fixed wide strip, so anything
          that is not 6.34:1 loses part of itself to the cover crop. */}
      {(desktopUsesFallback || (artDims && desktopVisiblePct < 95)) && (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            background: "#fff4e6",
            border: "1px solid #f3d9b4",
            borderRadius: 8,
            padding: "9px 12px",
            marginTop: 10,
          }}
        >
          <FiAlertTriangle size={14} style={{ color: "#f6961e", flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: 12, lineHeight: 1.5, color: "#4a5568" }}>
            {desktopUsesFallback
              ? `No desktop web artwork yet, so desktop web would show the phone artwork cropped to a wide strip: only about ${desktopVisiblePct}% of its height. Upload desktop web artwork (3159 by 498) to use this surface properly.`
              : artTallerThanFrame
                ? `Desktop web crops this artwork to a 3159 by 498 strip: only about ${desktopVisiblePct}% of its height is visible, centred. Re-crop it at that shape.`
                : `Desktop web crops this artwork to a 3159 by 498 strip: only about ${desktopVisiblePct}% of its width is visible, centred. Re-crop it at that shape.`}
          </span>
        </div>
      )}
    </div>
  );

  const RENDERERS = {
    app: appPreview,
    mobileweb: mobileWebPreview,
    desktopweb: desktopWebPreview,
  };

  const excluded = ["app", "mobileweb", "desktopweb"].filter(
    (key) => !surfaces.includes(key),
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {surfaces.map((key) => (
          <button key={key} type="button" style={tabStyle(surface === key)} onClick={() => setSurface(key)}>
            {SURFACE_META[key].icon} {SURFACE_META[key].label}
          </button>
        ))}
      </div>
      {surface ? RENDERERS[surface] : null}
      <div style={{ fontSize: 11, color: "#8898aa", marginTop: 10, textAlign: "center", lineHeight: 1.5 }}>
        Only the surfaces this banner is actually served to are shown
        {excluded.length > 0 && (
          <>
            {" "}— it will NOT appear on: {excluded.map((k) => SURFACE_META[k].label).join(", ")}
          </>
        )}
        . Layouts mirror the real client code.
      </div>
    </div>
  );
};

export default AdSimulation;
